// useAuthenticatedMedia — resolve a Matrix `mxc://` URI to a viewable
// `<img src>` against Synapse's v1.11 authenticated media endpoint.
//
// Background: Synapse ≥ 1.120 ships with `enable_authenticated_media:
// true`. The v3 unauthenticated `/_matrix/media/v3/download` path
// returns 404 in that mode; the v1 path requires an `Authorization:
// Bearer` header which `<img src=…>` cannot supply. The Element Web /
// Hydrogen-shaped fix is to fetch the bytes through the SDK (which
// holds the access token), wrap them in a `Blob`, and feed the
// resulting `URL.createObjectURL` to the `<img>`. This hook is the
// React seam for that flow.
//
// Lifecycle:
//   - The fetch starts when the (client, mxcUri, opts) tuple becomes
//     non-null and runs through the active client's `downloadMedia`.
//   - The resulting blob URL is returned via `src`.
//   - On unmount or mxcUri / opts change, a URL this hook created is
//     revoked and any in-flight fetch is dropped (`cancelled` flag —
//     there is no abort signal on `downloadMedia` itself yet).
//   - With `reuseAvatar`, the URL belongs to the shared avatar cache
//     (`~/lib/avatar-media`), which revokes it on eviction; the hook
//     only owns a fallback URL for downloads too large to cache.
//   - Errors surface as `error` and are otherwise non-fatal: the hook
//     stays at `{ src: null, isLoading: false, error }` until inputs
//     change.

import { useLayoutEffect, useRef, useState } from "react";
import { avatarMedia, cachedAvatarUrl, cachedReadyAvatarUrl } from "~/lib/avatar-media";

import type { Client } from "~/lib/matrix";

export interface AuthenticatedMediaThumbnail {
  w: number;
  h: number;
  method: "scale" | "crop";
}

export interface UseAuthenticatedMediaOptions {
  reuseAvatar?: boolean;
  thumbnail?: AuthenticatedMediaThumbnail;
  /** Mime type of the bytes. Used to label the constructed `Blob`. The
   *  SDK does not surface the upstream content-type, so callers should
   *  thread the original `info.mimetype` through when they have it.
   *  Defaults to `application/octet-stream`. */
  mimetype?: string;
}

export interface UseAuthenticatedMediaResult {
  /** `null` during initial fetch / on error. Populated with the
   *  consumer-owned object URL once the bytes resolve. */
  src: string | null;
  /** True from first non-null inputs until the fetch resolves or fails. */
  isLoading: boolean;
  /** Last fetch error. Cleared on successful resolution; sticky
   *  thereafter until `mxcUri` / `opts` change. */
  error: Error | null;
}

// State carries the canonical input key the result was produced for.
// Returning a `ready` URL whose key no longer matches the live inputs
// would briefly render the previous image after `mxcUri` changes
// (the next effect cleanup hasn't run yet on the synchronous render
// pass). Tagging every non-idle state with its key lets the consumer
// branch fall back to `loading` until the new effect resolves.
type State =
  | { status: "idle" }
  | { status: "loading"; key: string }
  | { status: "ready"; src: string; key: string }
  | { status: "failed"; error: Error; key: string };

// `Object.freeze` makes the singleton a true value type — a consumer
// that mistakenly mutates the result (e.g. `result.src = "..."`)
// fails fast in strict mode rather than silently corrupting the
// shared object every other call site reads.
const IDLE: UseAuthenticatedMediaResult = Object.freeze({
  src: null,
  isLoading: false,
  error: null,
});

// `clientId` lets the gate distinguish "same mxcUri under a new
// client" from "same client, same mxcUri". Without it, a sign-out /
// sign-in that re-renders the hook with the same `mxcUri` would
// briefly expose the previous session's blob URL because the
// previous-client `state.status === "ready"` matched the new
// client's input key. We use a `WeakMap`-backed lazy id rather than
// stringifying the client (which carries non-comparable internals)
// so the key stays cheap and stable.
const clientIds = new WeakMap<object, number>();
let nextClientId = 1;
function clientIdentity(client: object | null): string {
  if (!client) return "";
  let id = clientIds.get(client);
  if (id === undefined) {
    id = nextClientId++;
    clientIds.set(client, id);
  }
  return String(id);
}

function inputKey(
  client: object | null,
  mxcUri: string,
  thumbnailKey: string,
  mimetype: string | undefined,
): string {
  return `${clientIdentity(client)}|${mxcUri}|${thumbnailKey}|${mimetype ?? ""}`;
}

/**
 * Resolve a `mxc://` URI to an authenticated blob URL the browser can
 * load directly through `<img src>`.
 *
 * `null` `client` or `null` `mxcUri` short-circuits to the idle state
 * so call sites can branch on a single hook invocation rather than
 * conditionally calling the hook.
 *
 * Thumbnail / mimetype options identity-changing on every render is
 * tolerated — the hook canonicalises them through a JSON key so a new
 * `{ thumbnail: ... }` literal with the same shape does not retrigger
 * the fetch.
 */
export function useAuthenticatedMedia(
  client: Client | null,
  mxcUri: string | null,
  opts?: UseAuthenticatedMediaOptions,
): UseAuthenticatedMediaResult {
  const reuseAvatar = opts?.reuseAvatar ?? false;
  const thumbnail = opts?.thumbnail;
  const mimetype = opts?.mimetype;
  // Canonicalise the thumbnail shape into a stable string key so a
  // freshly-allocated `{ thumbnail: { w, h, method } }` literal with
  // the same numbers as last render does not refire the fetch. We
  // never compare the object identity directly.
  const thumbnailKey = thumbnail ? `${thumbnail.w}x${thumbnail.h}-${thumbnail.method}` : "file";
  // Refs let the effect read the latest live values even though the
  // dependency array references the canonicalised keys instead of
  // the literal objects. When `thumbnailKey` / `mimetype` change the
  // effect re-runs and reads fresh values; when the consumer
  // re-renders with an identity-different but value-equal opts, no
  // re-fetch happens.
  const thumbnailRef = useRef(thumbnail);
  thumbnailRef.current = thumbnail;
  const mimetypeRef = useRef(mimetype);
  mimetypeRef.current = mimetype;

  const [state, setState] = useState<State>(() => {
    if (!reuseAvatar || !client || !mxcUri) return { status: "idle" };
    const url = cachedReadyAvatarUrl(client, mxcUri, thumbnailRef.current);
    return url
      ? { status: "ready", src: url, key: inputKey(client, mxcUri, thumbnailKey, mimetype) }
      : { status: "idle" };
  });

  // Track the latest object URL so the cleanup pass can revoke
  // exactly the one this effect produced, even if state has moved on
  // by the time the cleanup runs.
  const objectUrlRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (!client || !mxcUri) {
      setState((prev) => (prev.status === "idle" ? prev : { status: "idle" }));
      return;
    }
    let cancelled = false;
    const key = inputKey(client, mxcUri, thumbnailKey, mimetype);
    const activeUri = mxcUri;
    const activeThumbnail = thumbnailRef.current;
    const activeMimetype = mimetypeRef.current;

    if (reuseAvatar) {
      const entry = avatarMedia(client, activeUri, activeThumbnail);
      const paint = (url: string | null | undefined) => {
        if (cancelled || !url) return;
        setState((prev) =>
          prev.status === "ready" && prev.key === key && prev.src === url
            ? prev
            : { status: "ready", src: url, key },
        );
      };
      if (entry.ready === "loaded" && entry.url) paint(entry.url);
      else
        void entry.promise
          .then(async () => {
            if (cancelled) return;
            const shared = cachedAvatarUrl(client, activeUri, activeThumbnail);
            if (shared) {
              const readiness = await entry.readiness;
              if (cancelled) return;
              if (readiness === "loaded") paint(shared);
              else
                setState({ status: "failed", error: new Error("Avatar image unavailable"), key });
              return;
            }
            const bytes = entry.media?.bytes;
            if (!bytes) return;
            const own = createObjectUrlFromBytes(bytes, activeMimetype);
            if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = own;
            paint(own);
          })
          .catch((err: unknown) => {
            if (cancelled) return;
            setState({
              status: "failed",
              error: err instanceof Error ? err : new Error(String(err)),
              key,
            });
          });
      return () => {
        cancelled = true;
        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current);
          objectUrlRef.current = null;
        }
      };
    }

    setState({ status: "loading", key });
    const source = { url: activeUri };
    const downloadOpts = activeThumbnail ? { thumbnail: activeThumbnail } : undefined;
    const ready = (result: { bytes: Uint8Array }) => {
      if (cancelled) return;
      const url = createObjectUrlFromBytes(result.bytes, activeMimetype);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = url;
      setState({ status: "ready", src: url, key });
    };
    void (async () => {
      try {
        const result = await client.bindings.downloadMedia(source, downloadOpts);
        ready(result);
      } catch (err) {
        if (cancelled) return;
        setState({
          status: "failed",
          error: err instanceof Error ? err : new Error(String(err)),
          key,
        });
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [client, mxcUri, thumbnailKey, mimetype, reuseAvatar]);

  if (!client || !mxcUri) return IDLE;
  // Gate ready/failed on the input key. When `mxcUri` (or
  // `thumbnailKey` / `mimetype`) changes mid-lifecycle, the effect
  // re-runs and writes a fresh `loading` state — but the synchronous
  // render that triggered the change happens BEFORE the effect, so
  // `state` still holds the previous resolution. Falling through to
  // `loading` until the keys match avoids briefly returning the
  // previous image's blob URL for the new input.
  const currentKey = inputKey(client, mxcUri, thumbnailKey, mimetype);
  if (state.status === "ready" && state.key === currentKey) {
    return { src: state.src, isLoading: false, error: null };
  }
  if (state.status === "failed" && state.key === currentKey) {
    return { src: null, isLoading: false, error: state.error };
  }
  // `idle` (initial render before the effect runs) and stale
  // ready/failed (effect for new inputs hasn't synced state yet)
  // are both treated as loading from the consumer's perspective:
  // a non-null mxcUri means a fetch is about to start, and the
  // placeholder branch is the correct rendering.
  return { src: null, isLoading: true, error: null };
}

function createObjectUrlFromBytes(bytes: Uint8Array, mimetype: string | undefined): string {
  // Copy the bytes into a fresh ArrayBuffer to detach from any
  // shared / reused buffer the SDK may hand back. `Uint8Array.slice`
  // would also work but the explicit copy reads more clearly.
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return URL.createObjectURL(new Blob([buffer], { type: mimetype || "application/octet-stream" }));
}
